import { ChangeEvent } from 'react';

import { CheckMarkIcon, HelpIcon, SettingsIcon, UsersIcon } from '@braskai/icons';
import { Typography, useDeviceType } from '@braskai/ui-kit';

import { Box, Stack } from '@mui/material';

import { FEATURES_MAP, FEATURES_PER_PLAN_CATEGORY } from '@/widgets/pricing/pricingCards/lib/constants';
import { getFormattedPlanPrice } from '@/widgets/pricing/pricingCards/lib/utils/getFormattedPlanPrice';

import { FeatureTooltip, useProducts } from '@/entities/pricing';

import { PaymentInterval, PlanCategory } from '@/shared/types/Billing';
import { Switch } from '@/shared/ui/Switch';

import { PRICING_CARD_DISCOUNT } from '../PricingCard/constants/pricingCardDiscound';
import { AtomIcon } from './assets/AtomIcon';
import { MinusIcon } from './assets/MinusIcon';

type ExpandedPlansFeaturesProps = {
  yearlySelected: boolean;
  onToggle: (yearlySelected: boolean) => void;
};

export const ExpandedPlansFeatures = ({ yearlySelected, onToggle }: ExpandedPlansFeaturesProps) => {
  const { isMobile } = useDeviceType();
  const { products } = useProducts(true);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onToggle(event.target.checked);
  };

  const renderFeatureRow = (item: { customFeatureName?: string; enabled: boolean }, key: string, index: number) => {
    const effectiveKey = ((item as { customFeatureName?: string }).customFeatureName ??
      key) as keyof typeof FEATURES_MAP;
    return (
      <Stack
        key={`${effectiveKey}-${index}`}
        sx={{
          borderBottom: '1px solid',
          borderBottomColor: 'grey.200',
          color: item.enabled ? 'grey.900' : 'grey.500',
          gap: 4,
          paddingY: 16,
          position: 'relative',
          width: '100%',
        }}
        alignItems="center"
        flexDirection="row"
      >
        {item.enabled ? <CheckMarkIcon height={20} width={20} /> : <MinusIcon height={20} width={20} />}
        {!isMobile && (
          <FeatureTooltip
            tooltipProps={FEATURES_MAP[effectiveKey].tooltip}
            onSendMixpanelEvent={FEATURES_MAP[effectiveKey]?.sendMixpanelEvent}
          >
            <Typography color={item.enabled ? 'grey.900' : 'grey.500'} variant="body-2-md">
              {FEATURES_MAP[effectiveKey].description}
            </Typography>
          </FeatureTooltip>
        )}
      </Stack>
    );
  };

  const renderFeatureSection = (section: Record<string, { customFeatureName?: string; enabled: boolean }[]>) => {
    return (
      <Stack flexDirection="column">
        {Object.entries(section).map(([key, feature]) => {
          const headerEffectiveKey = ((feature[0] as { customFeatureName?: string })?.customFeatureName ??
            key) as keyof typeof FEATURES_MAP;
          return (
            <Stack key={key}>
              {isMobile && (
                <FeatureTooltip
                  tooltipProps={FEATURES_MAP[headerEffectiveKey].tooltip}
                  onSendMixpanelEvent={FEATURES_MAP[headerEffectiveKey]?.sendMixpanelEvent}
                >
                  <Stack alignItems="center" flexDirection="row" gap={4} marginTop={12} paddingX={isMobile ? 16 : 40}>
                    <Typography color="grey.500" variant="body-2-md">
                      {FEATURES_MAP[headerEffectiveKey].description}
                    </Typography>
                    {FEATURES_MAP[headerEffectiveKey].tooltip && (
                      <Box color="grey.500">
                        <HelpIcon height={14} width={14} />
                      </Box>
                    )}
                  </Stack>
                </FeatureTooltip>
              )}
              <Stack
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  width: '100%',
                }}
                paddingX={isMobile ? 16 : 40}
              >
                {feature.map((item, index) =>
                  renderFeatureRow(item as { customFeatureName?: string; enabled: boolean }, key, index),
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack
      sx={{
        backgroundColor: 'grey.50',
        borderTop: '1px solid',
        borderTopColor: 'grey.200',
        paddingBottom: 24,
        width: '100%',
      }}
    >
      <Stack
        sx={{
          backgroundColor: 'grey.50',
          borderBottom: '1px solid',
          borderBottomColor: 'grey.200',
          position: 'sticky',
          top: 0,
          width: '100%',
          zIndex: 2,
        }}
      >
        <Stack margin="0 auto" maxWidth={1350} width="100%">
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            paddingBottom={16}
            paddingTop={24}
            paddingX={isMobile ? 16 : 40}
          >
            <Typography variant="title-4-sb">Compare plans</Typography>
            <Stack alignItems="center" flexDirection="row" gap={8}>
              <Typography variant="body-2-md">Billed annually</Typography>
              <Switch checked={yearlySelected} onChange={handleChange} />
            </Stack>
          </Stack>

          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              paddingBottom: 16,
              paddingX: isMobile ? 16 : 40,
              width: '100%',
            }}
          >
            {[PlanCategory.Creator, PlanCategory.CreatorPro, PlanCategory.Business, PlanCategory.Enterprise].map(
              (category) => {
                const paymentInterval = yearlySelected ? PaymentInterval.Year : PaymentInterval.Month;
                const categoryPlans =
                  products?.plan
                    .filter((p) => p.category === category && !p.is_archived)
                    .sort((a, b) => (a.metadata.bundle_position || 0) - (b.metadata.bundle_position || 0)) ?? [];
                const defaultPlan =
                  categoryPlans.find((p) => p.metadata.bundle_value_default) || categoryPlans[0] || undefined;
                const selectedPrice = defaultPlan
                  ? defaultPlan.prices.find(
                      (price) => price.status === 'active' && price.recurring?.interval === paymentInterval,
                    )
                  : undefined;
                const unit = selectedPrice?.unit_amount || 0;
                const originalPerMonth = getFormattedPlanPrice({
                  paymentInterval: selectedPrice?.recurring?.interval,
                  price: unit,
                });
                const discountCfg = PRICING_CARD_DISCOUNT[category as keyof typeof PRICING_CARD_DISCOUNT];
                const showDiscount = Boolean(
                  discountCfg && selectedPrice?.recurring?.interval === PaymentInterval.Year,
                );
                const discountedPerMonth = showDiscount
                  ? getFormattedPlanPrice({
                      paymentInterval: selectedPrice?.recurring?.interval,
                      price: Math.round(unit * (1 - discountCfg.year)),
                    })
                  : undefined;
                const isEnterpriseCategory = category === PlanCategory.Enterprise;
                let planName: string;
                switch (category) {
                  case PlanCategory.Business: {
                    planName = 'Business';
                    break;
                  }
                  case PlanCategory.Creator: {
                    planName = 'Creator';
                    break;
                  }
                  case PlanCategory.CreatorPro: {
                    planName = 'Creator Pro';
                    break;
                  }
                  default: {
                    planName = 'Enterprise';
                  }
                }

                let priceContent;
                if (!selectedPrice || isEnterpriseCategory || unit <= 0) {
                  priceContent = (
                    <Typography color="grey.500" variant="body-2-md">
                      Custom
                    </Typography>
                  );
                } else if (showDiscount && discountedPerMonth !== undefined) {
                  priceContent = (
                    <Typography color="grey.500" variant="body-2-md">
                      {`${isMobile ? '' : 'from '}$${discountedPerMonth}${isMobile ? '+' : ' '}/${isMobile ? 'mo' : 'month'}`}
                    </Typography>
                  );
                } else {
                  priceContent = (
                    <Typography color="grey.500" variant="body-2-md">
                      {`${isMobile ? '' : 'from '}$${originalPerMonth}${isMobile ? '+' : ' '}/${isMobile ? 'mo' : 'month'}`}
                    </Typography>
                  );
                }

                return (
                  <Stack key={category} flexDirection="column">
                    <Typography variant="body-2-md">{planName}</Typography>
                    {priceContent}
                  </Stack>
                );
              },
            )}
          </Stack>
        </Stack>
      </Stack>

      <Stack margin="0 auto" maxWidth={1350} width="100%">
        <Stack paddingX={isMobile ? 16 : 40} sx={{ width: '100%' }}>
          <Stack
            alignItems="center"
            flexDirection="row"
            gap={4}
            sx={{ borderBottom: '1px solid', borderBottomColor: 'grey.200', paddingY: 16, width: '100%' }}
          >
            <AtomIcon height={20} width={20} />
            <Typography variant="body-2-md">Credits and Generations</Typography>
          </Stack>
        </Stack>

        <Stack flexDirection="column">
          {isMobile && (
            <Stack paddingTop={16} paddingX={isMobile ? 16 : 40}>
              <Typography color="grey.500" variant="body-2-md">
                Extra minutes
              </Typography>
            </Stack>
          )}

          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              width: '100%',
            }}
            paddingX={isMobile ? 16 : 40}
          >
            {[PlanCategory.Creator, PlanCategory.CreatorPro, PlanCategory.Business, PlanCategory.Enterprise].map(
              (category) => {
                let content;
                switch (category) {
                  case PlanCategory.Creator: {
                    content = (
                      <>
                        <MinusIcon height={20} width={20} />
                        {!isMobile && (
                          <Typography color="grey.500" variant="body-2-md">
                            Extra minutes
                          </Typography>
                        )}
                      </>
                    );
                    break;
                  }
                  case PlanCategory.CreatorPro:
                  case PlanCategory.Business: {
                    content = (
                      <Typography variant="body-2-md">{isMobile ? '$3 /min' : '$3 per extra minute'}</Typography>
                    );
                    break;
                  }
                  default: {
                    content = <Typography variant="body-2-md">Custom</Typography>;
                  }
                }

                return (
                  <Stack
                    key={category}
                    sx={{
                      borderBottom: '1px solid',
                      borderBottomColor: 'grey.200',
                      gap: 4,
                      paddingY: 16,
                      position: 'relative',
                      width: '100%',
                    }}
                    alignItems="center"
                    flexDirection="row"
                  >
                    {content}
                  </Stack>
                );
              },
            )}
          </Stack>
        </Stack>
        <Stack flexDirection="column">
          {isMobile && (
            <Stack paddingTop={12} paddingX={isMobile ? 16 : 40}>
              <Typography color="grey.500" variant="body-2-md">
                Minutes included
              </Typography>
            </Stack>
          )}
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              width: '100%',
            }}
            paddingX={isMobile ? 16 : 40}
          >
            {[PlanCategory.Creator, PlanCategory.CreatorPro, PlanCategory.Business, PlanCategory.Enterprise].map(
              (category) => {
                const categoryPlans =
                  products?.plan
                    .filter((p) => p.category === category && !p.is_archived)
                    .sort((a, b) => (a.metadata.bundle_position || 0) - (b.metadata.bundle_position || 0)) ?? [];
                const minBundle = categoryPlans.length
                  ? Math.min(...categoryPlans.map((p) => Number(p.metadata.bundle_value)))
                  : undefined;
                const isEnterprise = category === PlanCategory.Enterprise;

                const content =
                  minBundle && !isEnterprise ? (
                    <Typography variant="body-2-md">{`from ${minBundle}${isMobile ? '' : ' minutes'}`}</Typography>
                  ) : (
                    <Typography variant="body-2-md">{`from 2000${isMobile ? '' : ' minutes'}`}</Typography>
                  );

                return (
                  <Stack
                    key={category}
                    sx={{
                      borderBottom: '1px solid',
                      borderBottomColor: 'grey.200',
                      gap: 4,
                      paddingY: 16,
                      position: 'relative',
                      width: '100%',
                    }}
                    alignItems="center"
                    flexDirection="row"
                  >
                    {content}
                  </Stack>
                );
              },
            )}
          </Stack>
        </Stack>
        {renderFeatureSection(FEATURES_PER_PLAN_CATEGORY.creditsAndGenerations as any)}

        <Stack paddingX={isMobile ? 16 : 40} sx={{ width: '100%' }}>
          <Stack
            alignItems="center"
            flexDirection="row"
            gap={4}
            sx={{ borderBottom: '1px solid', borderBottomColor: 'grey.200', paddingY: 16, width: '100%' }}
          >
            <SettingsIcon height={20} width={20} />
            <Typography variant="body-2-md">Editor</Typography>
          </Stack>
        </Stack>

        {renderFeatureSection(FEATURES_PER_PLAN_CATEGORY.editor as any)}

        <Stack paddingX={isMobile ? 16 : 40} sx={{ width: '100%' }}>
          <Stack
            alignItems="center"
            flexDirection="row"
            gap={4}
            sx={{ borderBottom: '1px solid', borderBottomColor: 'grey.200', paddingY: 16, width: '100%' }}
          >
            <UsersIcon height={20} width={20} />
            <Typography variant="body-2-md">Support and teamwork</Typography>
          </Stack>
        </Stack>

        {renderFeatureSection(FEATURES_PER_PLAN_CATEGORY.supportAndTeamwork as any)}
      </Stack>
    </Stack>
  );
};
